import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Paper, CircularProgress, Chip,
  FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
  Collapse, useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import { usePrivacyMask } from '../contexts/PrivacyMaskContext';
import { reconcileTradesForDisplay, detectWashSales, parseOptionSymbol, getClosedTradeNetPL } from '../utils/tradeUtils';

const DAY_MS = 86400000;
const LONG_TERM_DAYS = 365;

const fmtCur = (n, pm) => {
  if (pm) return '•••';
  const v = parseFloat(n) || 0;
  const abs = Math.abs(v);
  const str = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `-$${str}` : `$${str}`;
};

const fmtDate = (d) => {
  if (!d) return '-';
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split('-');
  return `${m}/${day}/${y.slice(2)}`;
};

// parseOptionSymbol returns expiration as YYMMDD
const fmtOptExp = (yymmdd) => {
  const s = String(yymmdd || '');
  if (s.length !== 6) return '';
  return `${s.slice(2, 4)}/${s.slice(4, 6)}/${s.slice(0, 2)}`;
};

// "1099-B box 1a" style description: option legs read naturally, stock shows qty
const describeTrade = (t) => {
  const raw = t.originalSymbol || t.original_symbol || '';
  const info = parseOptionSymbol(raw);
  if (info.isOption) {
    const exp = fmtOptExp(info.expiration);
    const strike = info.strike != null ? `$${info.strike}` : '';
    return `${info.underlying} ${exp} ${strike} ${info.type === 'C' ? 'Call' : 'Put'}`.trim();
  }
  return t.symbol || raw || '-';
};

const underlyingOf = (t) => {
  const info = parseOptionSymbol(t.originalSymbol || t.original_symbol || '');
  return String(info.isOption ? info.underlying : (t.symbol || info.underlying || '')).toUpperCase();
};

const normType = (t) => String(t.tradeType || t.trade_type || '').toUpperCase().replace(/[\s-]+/g, '_');
const isClosingRow = (t) =>
  ['SELL', 'STC', 'SELL_TO_CLOSE', 'BTC', 'BUY_TO_CLOSE', 'ASSIGNMENT', 'OASGN', 'OEXP', 'EXPIRE'].includes(normType(t)) ||
  !!(t._closedSide || t._isClosing);
const isDividendRow = (t) => normType(t) === 'DIVIDEND' || normType(t) === 'DIV';

const realizedOf = (t) => {
  const raw = t.netPL ?? t.net_pl ?? t.realizedPL ?? t.realized_pl ?? t._realizedSynth;
  if (raw === undefined || raw === null || raw === '' || isNaN(parseFloat(raw))) return null;
  return getClosedTradeNetPL(t);
};

const TaxProjection = () => {
  const theme = useTheme();
  const { privacyMask } = usePrivacyMask();
  const fc = (v) => fmtCur(v, privacyMask);
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [accounts, setAccounts] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAcct, setExpandedAcct] = useState({});
  const [showWashDetail, setShowWashDetail] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const acctResp = await apiRequest('/api/accounts?include_inactive=true');
      setAccounts(acctResp.data?.accounts || acctResp.data || []);

      // Pull a margin around the tax year so the ±30-day wash-sale window and
      // acquisitions opened before Jan 1 but closed this year are all visible.
      const baseUrl = `/api/trades?startDate=${year - 1}-12-01&endDate=${year + 1}-01-31&includeOpen=true`;
      const all = [];
      let offset = 0;
      const chunkSize = 500;
      let total = null;
      while (true) {
        const resp = await apiRequest(`${baseUrl}&limit=${chunkSize}&offset=${offset}`);
        const data = resp.data || {};
        const batch = data.trades || data || [];
        if (Array.isArray(batch)) all.push(...batch);
        if (typeof data.total === 'number') total = data.total;
        if ((total !== null && all.length >= total) || !Array.isArray(batch) || batch.length < chunkSize) break;
        offset += chunkSize;
      }
      // Client-side reconciliation fills in realizedPL on close rows that the
      // DB didn't compute (e.g. FIFO-matched manual lots).
      setTrades(reconcileTradesForDisplay(all));
    } catch (e) {
      console.error('Tax projection fetch failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const accountById = useMemo(() => {
    const m = new Map();
    accounts.forEach(a => m.set(String(a.id), a));
    return m;
  }, [accounts]);

  const isTaxableAccount = (accId) => {
    const acct = accountById.get(String(accId));
    if (!acct) return true;
    return String(acct.account_type || acct.accountType || 'taxable') !== 'non-taxable';
  };

  const washSaleMap = useMemo(
    () => detectWashSales(trades, { isTaxableAccount }),
    [trades, accounts] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Per-account 1099-B style projection
  const projections = useMemo(() => {
    const byAcct = new Map();
    const yearStart = `${year}-01-01`;
    const yearEnd = `${year}-12-31`;

    trades.forEach(t => {
      const accId = String(t.accountId || t.account_id || '');
      if (!accId) return;
      const acct = accountById.get(accId);
      if (!acct) return;

      if (!byAcct.has(accId)) {
        byAcct.set(accId, {
          account: acct,
          rows: [],
          stProceeds: 0, stBasis: 0, stGain: 0,
          ltProceeds: 0, ltBasis: 0, ltGain: 0,
          washDisallowed: 0, dividends: 0,
        });
      }
      const p = byAcct.get(accId);

      if (isDividendRow(t)) {
        const d = parseFloat(t.dividend || t.dividend_amount || 0) || 0;
        const dd = String(t.closingDate || t.tradeDate || t.trade_date || '').slice(0, 10);
        if (dd >= yearStart && dd <= yearEnd) p.dividends += d;
        return;
      }

      if (!isClosingRow(t)) return;
      const gl = realizedOf(t);
      if (gl === null) return;
      const closeDate = String(t.closingDate || t.closing_date || '').slice(0, 10);
      if (!closeDate || closeDate < yearStart || closeDate > yearEnd) return;

      const openDate = String(t.openDate || t.open_date || '').slice(0, 10);
      const heldDays = openDate
        ? (new Date(closeDate + 'T00:00:00') - new Date(openDate + 'T00:00:00')) / DAY_MS
        : 0;
      const longTerm = heldDays > LONG_TERM_DAYS;

      const proceeds = Math.abs(parseFloat(t.proceeds) || 0);
      // For sell-side closes proceeds come from the row; for buy-to-close rows
      // (short option buybacks) proceeds = buyback cost + gain.
      const proceedsShown = proceeds > 0 ? proceeds : Math.abs(parseFloat(t.cost) || 0) + gl;
      const basis = proceedsShown - gl;
      const wash = washSaleMap.get(t.id)?.disallowedLoss || 0;

      p.rows.push({
        id: t.id,
        description: describeTrade(t),
        underlying: underlyingOf(t),
        qty: Math.abs(parseFloat(t.quantity) || 0),
        openDate, closeDate, proceeds: proceedsShown, basis, gain: gl,
        wash, longTerm,
      });
      if (longTerm) {
        p.ltProceeds += proceedsShown; p.ltBasis += basis; p.ltGain += gl;
      } else {
        p.stProceeds += proceedsShown; p.stBasis += basis; p.stGain += gl;
      }
      p.washDisallowed += wash;
    });

    byAcct.forEach(p => {
      p.rows.sort((a, b) => a.closeDate.localeCompare(b.closeDate));
      // Group transactions by underlying for the 1099-B detail table
      const groupMap = new Map();
      p.rows.forEach(r => {
        const u = r.underlying || 'OTHER';
        if (!groupMap.has(u)) groupMap.set(u, { symbol: u, rows: [], gain: 0, wash: 0 });
        const g = groupMap.get(u);
        g.rows.push(r);
        g.gain += r.gain;
        g.wash += r.wash;
      });
      p.groups = [...groupMap.values()].sort((a, b) => a.symbol.localeCompare(b.symbol));
    });
    return [...byAcct.values()]
      .filter(p => isTaxableAccount(p.account.id))
      .sort((a, b) => a.account.name.localeCompare(b.account.name));
  }, [trades, accounts, year, washSaleMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    const t = { stGain: 0, ltGain: 0, washDisallowed: 0, dividends: 0, stProceeds: 0, ltProceeds: 0 };
    projections.forEach(p => {
      if (!isTaxableAccount(p.account.id)) return;
      t.stGain += p.stGain; t.ltGain += p.ltGain;
      t.washDisallowed += p.washDisallowed; t.dividends += p.dividends;
      t.stProceeds += p.stProceeds; t.ltProceeds += p.ltProceeds;
    });
    t.netGain = t.stGain + t.ltGain + t.washDisallowed; // disallowed loss adds back to taxable income
    return t;
  }, [projections, accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  const washList = useMemo(() => {
    const out = [];
    const tradeById = new Map(trades.map(t => [t.id, t]));
    washSaleMap.forEach((v, id) => {
      const t = tradeById.get(id);
      if (!t) return;
      const closeDate = String(t.closingDate || t.closing_date || '').slice(0, 10);
      if (!closeDate.startsWith(String(year))) return;
      const acct = accountById.get(String(t.accountId || t.account_id));
      out.push({
        id,
        account: acct?.name || `Acct ${t.accountId}`,
        description: describeTrade(t),
        closeDate,
        loss: -(v.disallowedLoss / (v.replacedQty / v.lossQty) || 0),
        disallowed: v.disallowedLoss,
        replacedQty: v.replacedQty,
        lossQty: v.lossQty,
        replacements: v.replacements,
      });
    });
    return out.sort((a, b) => a.closeDate.localeCompare(b.closeDate));
  }, [washSaleMap, trades, year, accountById]);

  const plColor = (v) => (v >= 0 ? 'success.main' : 'error.main');

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box />
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Tax Year</InputLabel>
          <Select value={year} label="Tax Year" onChange={e => setYear(e.target.value)}>
            {[0, 1, 2, 3].map(i => <MenuItem key={i} value={thisYear - i}>{thisYear - i}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchAll} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
        {loading && <CircularProgress size={20} />}
      </Box>

      {/* Combined taxable totals */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Estimated {year} taxable totals (excludes non-taxable accounts)
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box><Typography variant="caption" color="text.secondary">Short-term gain/loss</Typography>
              <Typography variant="h6" color={plColor(totals.stGain)}>{fc(totals.stGain)}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Long-term gain/loss</Typography>
              <Typography variant="h6" color={plColor(totals.ltGain)}>{fc(totals.ltGain)}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Wash sale disallowed</Typography>
              <Typography variant="h6" color="warning.main">{fc(totals.washDisallowed)}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Net capital gain/loss</Typography>
              <Typography variant="h6" color={plColor(totals.netGain)}>{fc(totals.netGain)}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Dividends (1099-DIV)</Typography>
              <Typography variant="h6">{fc(totals.dividends)}</Typography></Box>
          </Box>
        </CardContent>
      </Card>

      {/* Wash sales summary + detail */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => setShowWashDetail(v => !v)}>
            <WarningIcon color="warning" fontSize="small" />
            <Typography variant="subtitle2">
              Wash Sales: {washList.length} — {fc(washList.reduce((s, w) => s + w.disallowed, 0))} disallowed
            </Typography>
            {showWashDetail ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </Box>
          <Collapse in={showWashDetail}>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Account</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Sold</TableCell>
                    <TableCell align="right">Loss</TableCell>
                    <TableCell align="right">Disallowed</TableCell>
                    <TableCell>Replacement</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {washList.map(w => (
                    <TableRow key={w.id}>
                      <TableCell>{w.account}</TableCell>
                      <TableCell>{w.description}</TableCell>
                      <TableCell>{fmtDate(w.closeDate)}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>{fc(w.loss)}</TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main' }}>{fc(w.disallowed)}</TableCell>
                      <TableCell>
                        {w.replacements.map((r, i) => (
                          <Box key={i} component="span" sx={{ mr: 1 }}>
                            {r.trade.symbol} ×{r.qty}
                            {r.inIra && (
                              <Tooltip title="Repurchase inside a non-taxable account — loss permanently disallowed">
                                <Chip label="IRA" size="small" color="error" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} />
                              </Tooltip>
                            )}
                          </Box>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </CardContent>
      </Card>

      {/* Per-account projected 1099-B */}
      {projections.map(p => {
        const open = !!expandedAcct[p.account.id];
        return (
          <Card key={p.account.id} sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{p.account.name}</Typography>
                <Chip
                  label="Projected 1099-B"
                  size="small"
                  color="primary"
                  variant="contained"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
                {p.washDisallowed > 0 && (
                  <Chip label={`Wash disallowed ${fc(p.washDisallowed)}`} size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                )}
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" onClick={() => setExpandedAcct(s => ({ ...s, [p.account.id]: !open }))}>
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mt: 1 }}>
                <Box><Typography variant="caption" color="text.secondary">Short-term</Typography>
                  <Typography color={plColor(p.stGain)}>{fc(p.stGain)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Long-term</Typography>
                  <Typography color={plColor(p.ltGain)}>{fc(p.ltGain)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Proceeds</Typography>
                  <Typography>{fc(p.stProceeds + p.ltProceeds)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Dividends</Typography>
                  <Typography>{fc(p.dividends)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Transactions</Typography>
                  <Typography>{p.rows.length}</Typography></Box>
              </Box>

              <Collapse in={open}>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 480 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell>Acquired</TableCell>
                        <TableCell>Sold</TableCell>
                        <TableCell align="right">Proceeds</TableCell>
                        <TableCell align="right">Cost Basis</TableCell>
                        <TableCell align="right">Gain/Loss</TableCell>
                        <TableCell>Term</TableCell>
                        <TableCell align="right">Wash Adj</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {p.groups.map(g => (
                        <React.Fragment key={g.symbol}>
                          <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'grey.100' }}>
                            <TableCell colSpan={6} sx={{ py: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{g.symbol} ({g.rows.length})</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.5, color: plColor(g.gain), fontWeight: 'bold' }}>{fc(g.gain)}</TableCell>
                            <TableCell sx={{ py: 0.5 }} />
                            <TableCell align="right" sx={{ py: 0.5, color: g.wash > 0 ? 'warning.main' : 'text.secondary', fontWeight: 'bold' }}>
                              {g.wash > 0 ? fc(g.wash) : '-'}
                            </TableCell>
                          </TableRow>
                          {g.rows.map(r => (
                        <TableRow key={r.id} sx={{ bgcolor: r.wash > 0 ? (theme.palette.mode === 'dark' ? 'rgba(237,108,2,0.08)' : 'rgba(237,108,2,0.06)') : 'inherit' }}>
                          <TableCell>{r.description}</TableCell>
                          <TableCell align="right">{r.qty}</TableCell>
                          <TableCell>{fmtDate(r.openDate)}</TableCell>
                          <TableCell>{fmtDate(r.closeDate)}</TableCell>
                          <TableCell align="right">{fc(r.proceeds)}</TableCell>
                          <TableCell align="right">{fc(r.basis)}</TableCell>
                          <TableCell align="right" sx={{ color: plColor(r.gain) }}>{fc(r.gain)}</TableCell>
                          <TableCell>
                            <Chip label={r.longTerm ? 'Long' : 'Short'} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                          </TableCell>
                          <TableCell align="right" sx={{ color: r.wash > 0 ? 'warning.main' : 'text.secondary' }}>
                            {r.wash > 0 ? fc(r.wash) : '-'}
                          </TableCell>
                        </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}

      {!loading && projections.length === 0 && (
        <Typography color="text.secondary">No closed trades found for {year}.</Typography>
      )}
    </Box>
  );
};

export default TaxProjection;
